import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { invalidateCalendarOverlay } from '@/lib/queryInvalidation';
import { Icon } from '@/components/ui/Icon';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { ColorPicker } from '@/components/ui/ColorPicker';
import { Tooltip } from '@/components/ui/Tooltip';
import { MultiSelectDropdown } from '@/components/ui/MultiSelectDropdown';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import api from '@/services/api';
import { toast } from '@/utils/toast';

interface HolidaysModalProps {
  isOpen: boolean;
  onClose: () => void;
  calendarId: number;
  calendarName: string;
}

interface Country {
  code: string;
  name: string;
}

interface Festivity {
  id: number;
  countryCode: string;
  period: string;
  colorCode: string;
  status: string;
  _count: { holidays: number };
}

const DEFAULT_COLOR = '#3b82f6';

/**
 * Sanitize color value to prevent XSS via inline styles
 * Only allows valid 6-digit hex colors
 */
const sanitizeColor = (color: string): string => {
  return /^#[0-9A-Fa-f]{6}$/.test(color) ? color : DEFAULT_COLOR;
};

export const HolidaysModal = ({
  isOpen,
  onClose,
  calendarId,
  calendarName,
}: HolidaysModalProps) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('common');

  // Period options (moved inside component to access t())
  const PERIOD_OPTIONS = [
    { value: 'current', label: t('period_current_year') },
    { value: 'current-next', label: t('period_current_next') },
    { value: 'next-2', label: t('period_next_2_years') },
  ];

  // Form state
  const [selectedCountryIndices, setSelectedCountryIndices] = useState<number[]>([]);
  const [period, setPeriod] = useState('current-next');
  const [colorCode, setColorCode] = useState(DEFAULT_COLOR);
  const [editingColorId, setEditingColorId] = useState<number | null>(null);

  // Fetch countries
  const { data: countries = [] } = useQuery<Country[]>({
    queryKey: ['holiday-countries'],
    queryFn: async () => {
      const response = await api.get('/api/holidays/countries');
      return response.data.data;
    },
    enabled: isOpen,
  });

  // Fetch existing festivities
  const { data: festivities = [], isLoading: loadingFestivities } = useQuery<Festivity[]>({
    queryKey: ['festivities', calendarId],
    queryFn: async () => {
      const response = await api.get(`/api/holidays/festivities/${calendarId}`);
      return response.data.data;
    },
    enabled: isOpen && calendarId > 0,
  });

  // Create festivity mutation
  const createMutation = useMutation({
    mutationFn: async (data: { calendarId: number; countryCode: string; period: string; colorCode: string }) => {
      const response = await api.post('/api/holidays/festivities', data);
      return response.data.data;
    },
    onSuccess: (data) => {
      toast.success(t('holidays_count', { count: data.holidaysImported }));
      invalidateCalendarOverlay(queryClient);
    },
    onError: () => {
      toast.error(t('failed_to_add_holidays'));
    },
  });

  // Toggle mutation
  const toggleMutation = useMutation({
    mutationFn: async ({ id, active }: { id: number; active: boolean }) => {
      const response = await api.patch(`/api/holidays/festivities/${id}/toggle`, { active });
      return response.data.data;
    },
    onSuccess: () => {
      invalidateCalendarOverlay(queryClient);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/holidays/festivities/${id}`);
    },
    onSuccess: () => {
      toast.success(t('holidays_removed'));
      invalidateCalendarOverlay(queryClient);
    },
  });

  // Color update mutation
  const colorMutation = useMutation({
    mutationFn: async ({ id, colorCode }: { id: number; colorCode: string }) => {
      const response = await api.patch(`/api/holidays/festivities/${id}/color`, { colorCode });
      return response.data.data;
    },
    onSuccess: () => {
      invalidateCalendarOverlay(queryClient);
    },
  });

  // Convert countries to MultiSelectDropdown format
  const countryOptions = countries.map((c, index) => ({
    id: index,
    name: `${c.name} (${c.code})`,
    subtitle: c.code,
  }));

  // Get selected country codes from indices
  const getSelectedCountryCodes = () => {
    return selectedCountryIndices.map(index => countries[index]?.code).filter(Boolean);
  };

  // Get country name by code
  const getCountryName = (code: string) => countries.find((c) => c.code === code)?.name || code;

  // Handle add holidays
  const handleAdd = async () => {
    const selectedCodes = getSelectedCountryCodes();
    if (selectedCodes.length === 0) {
      toast.error(t('select_country'));
      return;
    }

    // Add holidays for each selected country
    for (const countryCode of selectedCodes) {
      await createMutation.mutateAsync({
        calendarId,
        countryCode,
        period,
        colorCode,
      });
    }
    setSelectedCountryIndices([]);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 p-6 max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-full">
              <Icon name={ICONS.FLAG} size="md" color="text-primary-600" ariaHidden />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{t('manage_holidays')}</h2>
              <p className="text-sm text-gray-500">{calendarName}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <Icon name={ICONS.CLOSE} size="sm" ariaHidden />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto space-y-6">
          {/* Add Holidays Section */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">{t('add_holidays')}</h3>

            {/* Country Multi-Select */}
            <div className="mb-3">
              <MultiSelectDropdown
                value={selectedCountryIndices}
                onChange={setSelectedCountryIndices}
                options={countryOptions}
                placeholder={t('select_countries')}
                isLoading={!countries.length}
              />
            </div>

            {/* Period & Color */}
            <div className="flex flex-col sm:grid sm:grid-cols-2 gap-3 mb-3">
              <Select
                value={period}
                onValueChange={setPeriod}
                options={PERIOD_OPTIONS}
                size="sm"
              />
              <ColorPicker
                value={colorCode}
                onChange={setColorCode}
              />
            </div>

            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={handleAdd}
                disabled={selectedCountryIndices.length === 0 || createMutation.isPending}
              >
                {createMutation.isPending
                  ? t('adding')
                  : selectedCountryIndices.length > 1
                    ? t('add_holidays_for_countries', { count: selectedCountryIndices.length })
                    : t('add_holidays')
                }
              </Button>
            </div>
          </div>

          {/* Existing Festivities */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              {t('active_holiday_sets')} ({festivities.length})
            </h3>

            {loadingFestivities ? (
              <p className="text-sm text-gray-500">{t('loading')}</p>
            ) : festivities.length === 0 ? (
              <p className="text-sm text-gray-500 italic">{t('no_holidays_yet')}</p>
            ) : (
              <div className="space-y-2">
                {festivities.map((f) => (
                  <div
                    key={f.id}
                    className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        {editingColorId === f.id ? (
                          <div className="relative">
                            <ColorPicker
                              value={f.colorCode}
                              onChange={(color) => {
                                // Only update if valid color
                                if (/^#[0-9A-Fa-f]{6}$/.test(color)) {
                                  colorMutation.mutate({ id: f.id, colorCode: color });
                                  setEditingColorId(null);
                                }
                              }}
                            />
                            <Tooltip content={t('cancel')}>
                              {/* eslint-disable i18next/no-literal-string */}
                              <button
                                onClick={() => setEditingColorId(null)}
                                className="absolute top-0 right-0 -mt-2 -mr-2 text-gray-500 hover:text-gray-700 bg-white rounded-full w-5 h-5 flex items-center justify-center shadow-sm"
                                aria-label={t('cancel')}
                              >
                                ✕
                              </button>
                              {/* eslint-enable i18next/no-literal-string */}
                            </Tooltip>
                          </div>
                        ) : (
                          <Tooltip content={t('change_color')}>
                            <button
                              type="button"
                              onClick={() => setEditingColorId(f.id)}
                              className="w-8 h-8 rounded border border-gray-300 cursor-pointer hover:ring-2 hover:ring-gray-400 transition-all"
                              style={{ backgroundColor: sanitizeColor(f.colorCode) }}
                              aria-label={t('change_color')}
                            />
                          </Tooltip>
                        )}
                      </div>
                      <div>
                        <span className="font-medium text-gray-900">
                          {getCountryName(f.countryCode)}
                        </span>
                        <span className="text-sm text-gray-500 ml-2">
                          {/* eslint-disable-next-line i18next/no-literal-string */}
                          ({f._count.holidays} holidays)
                        </span>
                        <p className="text-xs text-gray-400">
                          {PERIOD_OPTIONS.find((p) => p.value === f.period)?.label}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleMutation.mutate({ id: f.id, active: f.status !== 'active' })}
                        className={cn(
                          'relative w-10 h-6 rounded-full transition-colors',
                          f.status === 'active' ? 'bg-primary-500' : 'bg-gray-300'
                        )}
                      >
                        <span
                          className={cn(
                            'absolute top-1 w-4 h-4 bg-white rounded-full transition-transform',
                            f.status === 'active' ? 'left-5' : 'left-1'
                          )}
                        />
                      </button>
                      <button
                        onClick={() => deleteMutation.mutate(f.id)}
                        className="p-2 text-gray-400 hover:text-bs-danger rounded-lg transition-colors"
                      >
                        <Icon name={ICONS.TRASH} size="sm" ariaHidden />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end pt-4 border-t mt-4">
          <Button variant="secondary" onClick={onClose}>
            {t('done')}
          </Button>
        </div>
      </div>
    </div>
  );
};
